import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import axios from 'axios';
import { BASE_API_URL } from '../../utils/constants';
import { logoutUser } from './authService';
import { myFirebaseAuth } from '../../firebase/config';
import { firebaseFileUploadHelper } from '../../utils/helpers';
import { FiledLienTemplateField } from '@dill/dill-shared';

export const createFiledLienTemplateByAdmin = createAsyncThunk(
  'filedLienTemplates/createFiledLienTemplateByAdmin',
  async (
    {
      supplierId,
      originalPdfUrl,
      modifiedPdfUrl,
      formFields,
      name,
    }: {
      supplierId: string;
      originalPdfUrl: string;
      name: string;
      modifiedPdfUrl: string;
      formFields: FiledLienTemplateField[];
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const downloadUrl = await firebaseFileUploadHelper({
        blobUrl: modifiedPdfUrl,
        fileNameWithExtension: `${new Date().getTime()}-${name}.pdf`,
        storagePath: 'filedLienTemplates',
      });
      if (!downloadUrl) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/filedLienTemplates/createFiledLienTemplatesByAdmin`,
        { originalPdfUrl, modifiedPdfUrl: downloadUrl, formFields, name },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const createSupplierFiledLienTemplate = createAsyncThunk(
  'filedLienTemplates/createSupplierFiledLienTemplate',
  async (
    {
      supplierId,
      pdfUrl,
      name,
    }: {
      supplierId: string;
      pdfUrl: string;
      name: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const downloadUrl = await firebaseFileUploadHelper({
        blobUrl: pdfUrl,
        fileNameWithExtension: `${new Date().getTime()}-${name}.pdf`,
        storagePath: 'filedLienTemplates',
      });
      if (!downloadUrl) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/filedLienTemplates/createSupplierFiledLienTemplate`,
        { pdfUrl: downloadUrl, name },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getFiledLienTemplates = createAsyncThunk(
  'filedLienTemplates/getFiledLienTemplates',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/filedLienTemplates/getFiledLienTemplates`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getFiledLienTemplatesByAdmin = createAsyncThunk(
  'filedLienTemplates/getFiledLienTemplatesByAdmin',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/filedLienTemplates/getFiledLienTemplatesByAdmin?status=pending`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const updateFiledLienTemplateByAdmin = createAsyncThunk(
  'filedLienTemplates/updateFiledLienTemplateByAdmin',
  async (
    {
      supplierId,
      templateId,
      modifiedPdfUrl,
      formFields,
    }: {
      supplierId: string;
      templateId: string;
      modifiedPdfUrl: string;
      formFields: FiledLienTemplateField[];
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const downloadUrl = await firebaseFileUploadHelper({
        blobUrl: modifiedPdfUrl,
        fileNameWithExtension: `${new Date().getTime()}-${name}.pdf`,
        storagePath: 'filedLienTemplates',
      });
      if (!downloadUrl) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/filedLienTemplates/updateFiledLienTemplatesByAdmin/${templateId}`,
        { modifiedPdfUrl: downloadUrl, formFields },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
